/**
 * Карточка разговора «Голосового стенда» (`?tab=calls&call=<комната>&ctab=cost|prompt`): вкладки «Обзор»
 * (сводка, запись MP3 / OGG, одна лента — текст модели и что прозвучало в записи по GigaAM в одном пузыре,
 * поиск по базе знаний с карточками ссылками), «Расход» (₽ по курсу ЦБ на день разговора и доли),
 * «Инструкция». `GET /api/voice-lab/calls/{room}`. Ревью Codex 886 —
 * `docs/specs/2026-10-27-voice-lab-call-card-ux-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { MessageSquarePlus } from "lucide-react";
import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { ChecklistBox } from "@/components/ChecklistBox";
import { DealSide } from "@/components/DealSide";
import { ApiFailure } from "@/lib/api";
import {
  END_REASONS,
  MODES,
  OUTCOMES,
  labAudioUrl,
  labCallQuery,
  labMp3Url,
  type LabAction,
  type LabCallCard,
  type LabEvent,
  type LabLinkSend,
  type LabRemark,
  templateHref,
} from "@/lib/voiceLab";
import { wordDiff, type DiffWord } from "@/lib/wordDiff";

import { VoiceLabCallLog } from "./VoiceLabCallLog";
import { ДОЛГО, Метка, ПОИСК, ToolStep, rub, usd } from "./VoiceLabParts";
import { PbxTab } from "./VoiceLabPbx";
import { RemarkChip, RemarkForm, RemarksTab } from "./VoiceLabRemarks";

type Реплика = Extract<LabEvent, { type: "turn" }>;
type Инструмент = Extract<LabEvent, { type: "tool" }>;
type Лишняя = NonNullable<LabCallCard["heard_extra"]>[number];
type Элемент =
  | (Реплика & { kind: "turn"; i: number })
  | (Инструмент & { kind: "tool"; ti: number })
  | (Лишняя & { kind: "extra"; t: number })
  | { kind: "remark"; r: LabRemark; t: number }
  | { kind: "end"; t: number };
// Откуда ответ робота: шаги между прошлой репликой робота и этой (спека 2026-11-03-voice-lab-remark-loop).
// Без шагов — ответ из текста инструкции: «Частые ответы» или своими словами — модель не сообщает.
const НЕ_НАШЛОСЬ = "В базе ответа не нашлось";
const ИСТОЧНИК: Record<string, (t: Инструмент) => string> = {
  search_knowledge: (t) => (t.error || t.result.startsWith(НЕ_НАШЛОСЬ) ? "LightRAG · не нашёл" : "LightRAG · нашёл"),
  find_answer: (t) => (t.result.startsWith(НЕ_НАШЛОСЬ) ? "база знаний · не нашёл" : "база знаний · нашёл"),
  topic_answer: (t) => `тема ${String(t.args.topic ?? "")}`.trim(),
  find_products: () => "подбор товара",
  find_by_photo: () => "поиск по фото",
  salon_info: () => "справка салона",
  free_slots: () => "свободное время",
};
const ИНСТРУКЦИЯ = "без поиска — из инструкции";

export function источники(c: Pick<LabCallCard, "turns" | "tools">): Map<number, string> {
  const out = new Map<number, string>();
  const как = черновики(c);
  let с = "";
  c.turns.forEach((t, i) => {
    if (t.who !== "agent") return;
    const шаги = c.tools.filter((x) => x.at > с && x.at <= t.at && ИСТОЧНИК[x.name]);
    с = t.at;
    if (t.phrase) return; // готовая фраза записью — источник не нужен
    const ч = как.get(i);
    if (!шаги.length && ч && (ч.args.template_id != null || ч.result.startsWith("Шаблон #"))) return; // ссылка «📋»
    out.set(i, шаги.length ? [...new Set(шаги.map((x) => ИСТОЧНИК[x.name]!(x)))].join(" · ") : ИНСТРУКЦИЯ);
  });
  return out;
}

/** Черновик «Чатов» в боте (шаг `draft`) перед репликой робота: по шаблону AI или по линии сценария. */
export function черновики(c: Pick<LabCallCard, "turns" | "tools">): Map<number, Инструмент> {
  const out = new Map<number, Инструмент>();
  let с = "";
  c.turns.forEach((t, i) => {
    if (t.who !== "agent") return;
    const шаг = c.tools.filter((x) => x.at > с && x.at <= t.at && x.name === "draft").at(-1);
    с = t.at;
    if (шаг) out.set(i, шаг);
  });
  return out;
}

/** «📋 шаблон #2 v1 · дословно» — к шаблону и его замечаниям; «✍️ линия chat/zapis v5» — к версии линии. */
function Черновик({ шаг }: { шаг: Инструмент }) {
  const a = { ...(шаг.args as Record<string, unknown>) };
  const ссылка = "text-accent hover:underline";
  const было = /^Шаблон #(\d+) v(\d+)/.exec(шаг.result); // разговоры до 04.11.26: номер — только в пояснении
  if (a.template_id == null && было) {
    a.template_id = Number(было[1]);
    a.template_version = Number(было[2]);
    a.verbatim = !шаг.result.includes("отступил от шаблона");
  }
  if (a.template_id != null) {
    const id = Number(a.template_id);
    return (
      <Link
        to={templateHref(id, true)}
        className={ссылка}
        title="Ответ собран по шаблону AI — открыть шаблон и оставить к нему замечание"
        data-annot="шаблон ответа"
      >
        📋 шаблон #{id} v{String(a.template_version ?? "")}
        {a.verbatim === false ? " · ⚠ отступил" : a.verbatim ? " · дословно" : ""} ↗
      </Link>
    );
  }
  const [линия, версия] = String(a.line ?? "").split(" ");
  if (!линия || !версия || !линия.includes("/")) return null;
  return (
    <Link
      to={`/voice-lab?tab=scenarios&line=${encodeURIComponent(линия)}&v=${версия}`}
      className={ссылка}
      title="Ответ сгенерирован по линии сценария — открыть версию"
      data-annot="линия ответа"
    >
      ✍️ линия {линия} {версия} ↗
    </Link>
  );
}

/** Форма замечания открыта: у реплики `turn` или у плеера на секунде `t`. */
type Форма = { turn: number } | { t: number } | null;

const кнопка = (on: boolean) =>
  `shrink-0 rounded-full border px-3 py-1 text-[12.5px] ${on ? "border-accent bg-accent/10 text-fg" : "border-border text-fg-muted hover:text-fg"}`;
const МЕТКА = "rounded-sm border-b border-accent bg-warn-bg px-0.5 text-fg";
const ЖДЁМ_РАСШИФРОВКУ = 15 * 60_000; // столько после конца разговора карточка сама переспрашивает

export function телефон(n: string): string {
  return n.length === 11
    ? `+${n[0]} ${n.slice(1, 4)} ${n.slice(4, 7)}-${n.slice(7, 9)}-${n.slice(9)}`
    : n;
}

export function когда(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" })} ${d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}`;
}

export function длительность(s: number | null): string {
  if (s == null) return "—";
  return s < 60 ? `${s} с` : `${Math.floor(s / 60)} мин ${s % 60} с`;
}

const мин = (t: number) =>
  `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}`;

const КАНАЛ: Record<string, string> = { whatsapp: "WhatsApp", telegram: "Telegram", max: "MAX", has_link: "—" };
const ОТПРАВКА: Record<string, string> = {
  wait: "ждёт отправки",
  work: "отправляется",
  sent: "отправлено",
  preview: "не отправлено (проба)",
  no_chat: "нет чата канала",
  error: "не ушло",
  has_link: "ссылка у клиентки уже есть",
};

/** Ссылки на самозапись, которые робот пообещал в звонке: куда, что ушло, чат. */
function Ссылки({ links }: { links: LabLinkSend[] }) {
  return (
    <div className="flex flex-col gap-1 text-[12.5px]" data-annot="ссылки на запись из звонка">
      {links.map((l) => (
        <div key={l.id} className="flex flex-col gap-0.5">
          <div>
            Ссылка на запись → {КАНАЛ[l.channel] ?? l.channel}:{" "}
            <b className={l.status === "sent" ? "text-ok" : l.status === "error" || l.status === "no_chat" ? "text-err" : ""}>
              {ОТПРАВКА[l.status] ?? l.status}
            </b>
            {l.date_send && ` · ${l.date_send.slice(11, 16)}`}
            {l.chat_id && (
              <>
                {" · "}
                <a href={`/chats/${l.chat_id}`} className="text-accent hover:underline">
                  чат
                </a>
              </>
            )}
            {l.error && <span className="text-fg-muted"> · {l.error}</span>}
          </div>
          {l.text && <div className="whitespace-pre-wrap text-fg-muted">{l.text}</div>}
        </div>
      ))}
    </div>
  );
}

/** Учтённый расход: модель и распознавание (журнал) + эмбеддинги поиска по базе; null — у модели нет цены.
 * В `cost.usd` журнала эмбеддинга нет (`voicelab/price.py`), поэтому прибавляем его здесь один раз. */
function итог(c: LabCallCard): { usd: number | null; поиск: number; запросов: number; неоценён: boolean } {
  const шаги = c.tools.filter((t) => ПОИСК.has(t.name));
  const оценены = шаги.filter((t) => t.embed?.usd != null);
  const поиск = оценены.reduce((s, t) => s + (t.embed?.usd ?? 0), 0);
  const модель = c.cost?.usd;
  return {
    usd: модель != null ? модель + поиск : null,
    поиск,
    запросов: оценены.length,
    неоценён: оценены.length < шаги.length,
  };
}

function Поле({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col">
      <dt className="text-[11.5px] text-fg-muted">{label}</dt>
      <dd className="text-[12.5px] [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

/** Сводка на «Обзоре»: подписанные поля в постоянном порядке вместо строки через точки. */
function Сводка({
  c,
  toCost,
  toPrompt,
  seek,
}: {
  c: LabCallCard;
  toCost: () => void;
  toPrompt: () => void;
  seek?: ((t: number) => void) | undefined;
}) {
  const r = c.row;
  const сумма = итог(c);
  const курс = c.rub?.rate;
  // последнее действие робота в CRM во время разговора: итог робота и факт в Bitrix — разные вещи
  const crm = [...(c.actions ?? [])].reverse().find((a) => a.kind === "crm");
  return (
    <dl className="grid max-w-3xl grid-cols-2 gap-x-5 gap-y-1.5 sm:grid-cols-3" data-annot="сводка разговора">
      <Поле label="Итог · по словам робота">
        <b>{OUTCOMES[r.outcome] ?? r.outcome}</b>
        {crm && (
          <div className={crm.status === "applied" ? "text-ok" : crm.status === "error" ? "text-err" : "text-fg-muted"}>
            CRM: {crm.status_label}
          </div>
        )}
      </Поле>
      <Поле label="Длительность звонка">{длительность(r.seconds)}</Поле>
      <Поле label="Модель · голос">
        {r.model}
        {r.voice && ` · ${r.voice}`}
        {r.speed != null && r.speed !== 1 && ` ×${r.speed.toFixed(2)}`}
      </Поле>
      <Поле label="Режим">{MODES[r.mode] ?? r.mode}</Поле>
      <Поле label="Средняя пауза">
        <span className={`tabular-nums ${r.latency_avg != null && r.latency_avg > ДОЛГО ? "text-err" : ""}`}>
          {r.latency_avg != null ? `${r.latency_avg.toLocaleString("ru-RU")} мс` : "—"}
        </span>
      </Поле>
      <Поле label={сумма.неоценён ? "Известный расход" : "Учтённый расход"}>
        <button type="button" onClick={toCost} className="tabular-nums text-accent hover:underline">
          {сумма.usd != null ? (курс ? rub(сумма.usd, курс) : usd(сумма.usd)) : "цена модели не задана"}
        </button>
        {сумма.usd != null && курс && r.seconds ? (
          <span className="tabular-nums text-fg-muted"> · {rub((сумма.usd * 60) / r.seconds, курс)}/мин</span>
        ) : null}
      </Поле>
      <Поле label="Инструкция">
        {r.prompt && c.instructions ? (
          <button type="button" onClick={toPrompt} className="text-accent hover:underline">
            {r.prompt}
          </button>
        ) : (
          (r.prompt ?? "не сохранена")
        )}
      </Поле>
      <Поле label="Конец">
        {r.end_reason ? (END_REASONS[r.end_reason] ?? r.end_reason) : "—"}
        {c.end_t != null && (
          <>
            {" · "}
            {seek ? (
              <button type="button" onClick={() => seek(c.end_t!)} className="tabular-nums text-accent hover:underline">
                {мин(c.end_t)} записи
              </button>
            ) : (
              `${мин(c.end_t)} записи`
            )}
          </>
        )}
      </Поле>
      {r.turn === "text" ? (
        <Поле label="Канал">текст (Telegram @urtoperatorbot)</Поле>
      ) : (
        <Поле label="Перебивания">{r.turn === "livekit" ? "LiveKit" : "определяет модель"}</Поле>
      )}
    </dl>
  );
}

function Слова({ words }: { words: DiffWord[] }) {
  return (
    <>
      {words.map((w, i) => (
        <span key={i}>
          {i > 0 && " "}
          {w.same ? w.text : <mark className={МЕТКА}>{w.text}</mark>}
        </span>
      ))}
    </>
  );
}

/** Пузырь реплики: текст модели и под ним — что прозвучало в записи. Различающиеся слова подсвечены в обоих
 * текстах одинаково: ошибиться может и модель, и GigaAM — вердикт даёт только прослушивание. */
function Пузырь({
  e,
  i,
  room,
  seek,
  сверка,
  есть_запись,
  замечания,
  форма,
  setФорма,
  источник,
  черновик,
}: {
  e: Реплика;
  i: number;
  room: string;
  seek?: ((t: number) => void) | undefined;
  сверка: boolean;
  есть_запись: boolean;
  замечания: LabRemark[];
  форма: boolean;
  setФорма: (f: Форма) => void;
  источник?: string | undefined;
  черновик?: Инструмент | undefined;
}) {
  const робот = e.who === "agent";
  const текст = e.text || "";
  const [раскрыть, setРаскрыть] = useState(false);
  const diff = есть_запись && e.heard ? wordDiff(текст, e.heard) : null;
  const различаются = !!diff && !diff.equal;
  const нет_в_записи = есть_запись && !e.heard && !!текст && e.t != null;
  return (
    <div className={`group flex flex-col gap-1 text-[13px] ${робот ? "items-start" : "items-end"}`}>
      <div
        className={`flex min-w-0 max-w-[94%] flex-col gap-1 rounded-[var(--radius-ui)] px-3 py-1.5 [overflow-wrap:anywhere] sm:max-w-[80%] ${робот ? "bg-bg-subtle" : "bg-accent/10"} ${форма ? "ring-1 ring-accent" : ""}`}
      >
        <div className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-fg-muted">
          <Метка t={e.heard_start ?? e.t} seek={seek} />
          <span>{робот ? "Робот" : "Собеседник"}</span>
          {сверка && различаются && (
            <span className="rounded border border-border bg-warn-bg px-1 text-fg">тексты различаются</span>
          )}
          {e.interrupted && (
            <span className="text-err" title="Звук оборвался раньше текста — что прозвучало, слушайте в записи">
              перебили
            </span>
          )}
          {e.echo && (
            <span title="Собеседник «сказал» слова робота сразу после него — похоже на эхо в трубке; сверьте на слух">
              возможное эхо
            </span>
          )}
          {e.phrase && (
            <span className="text-accent" title={`Прозвучала готовой записью из библиотеки фраз (${e.phrase}) — без озвучки моделью`}>
              запись
            </span>
          )}
          {источник && (
            <span
              className={источник === ИНСТРУКЦИЯ ? "text-warn" : ""}
              title={
                источник === ИНСТРУКЦИЯ
                  ? "Перед ответом робот не искал: ответ из текста инструкции («Частые ответы») или своими словами — модель источник не сообщает"
                  : "Шаги робота перед этим ответом"
              }
              data-annot="источник ответа"
            >
              ⓘ {источник}
            </span>
          )}
          {черновик && <Черновик шаг={черновик} />}
          {робот && e.latency_ms != null && (
            <span className={e.latency_ms > ДОЛГО ? "text-err" : ""}>пауза {e.latency_ms.toLocaleString("ru-RU")} мс</span>
          )}
          {!!замечания.length && (
            <span className="text-accent" title="Замечаний к реплике">
              ✎ {замечания.length}
            </span>
          )}
          <button
            type="button"
            onClick={() => setФорма(форма ? null : { turn: i })}
            className="ml-auto flex items-center gap-1 rounded-full px-1.5 text-accent opacity-0 hover:bg-bg-hover focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
            title="Оставить замечание к этой реплике — голосом или текстом"
          >
            <MessageSquarePlus size={13} aria-hidden /> Замечание
          </button>
        </div>
        {сверка && (различаются || нет_в_записи) && (
          <div className="text-[11px] text-fg-muted">{робот ? "Текст модели" : "Модель услышала"}</div>
        )}
        <div className="whitespace-pre-wrap">
          {сверка && различаются ? <Слова words={diff.a} /> : текст || <span className="text-fg-muted">пустая реплика в журнале</span>}
        </div>
        {сверка && diff && (
          <div className={различаются ? "border-t border-border pt-1" : ""}>
            <div className="text-[11px] text-fg-muted">
              Запись · GigaAM
              {!различаются && (
                <>
                  {" · тексты совпали "}
                  <button type="button" onClick={() => setРаскрыть(!раскрыть)} className="text-accent hover:underline">
                    {раскрыть ? "скрыть" : "показать"}
                  </button>
                </>
              )}
            </div>
            {(различаются || раскрыть) && (
              <div className="whitespace-pre-wrap text-[12.5px]">
                <Слова words={diff.b} />
              </div>
            )}
          </div>
        )}
        {сверка && нет_в_записи && (
          <div className="border-t border-border pt-1 text-[11px] text-fg-muted">
            Фрагмент записи не сопоставлен{робот ? "" : " — вероятно, слился с соседней фразой"}
          </div>
        )}
      </div>
      {замечания.map((r) => (
        <RemarkChip key={r.id} r={r} seek={seek} />
      ))}
      {форма && <RemarkForm room={room} turn={i} t={e.heard_start ?? e.t ?? null} onClose={() => setФорма(null)} />}
    </div>
  );
}

function ТолькоВЗаписи({ e, seek }: { e: Лишняя; seek?: ((t: number) => void) | undefined }) {
  const робот = e.who === "agent";
  return (
    <div className={`flex text-[13px] ${робот ? "" : "justify-end"}`}>
      <div className="min-w-0 max-w-[94%] rounded-[var(--radius-ui)] border border-dashed border-accent px-3 py-1.5 [overflow-wrap:anywhere] sm:max-w-[80%]">
        <div className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-fg-muted">
          <Метка t={e.start} seek={seek} />
          <span>{робот ? "Робот" : "Собеседник"} · только в записи</span>
        </div>
        <div>{e.text}</div>
        <div className="text-[11px] text-fg-muted">В журнале модели такой реплики нет</div>
      </div>
    </div>
  );
}

const ЦВЕТ_ДЕЙСТВИЯ: Record<string, string> = {
  applied: "border-ok/60",
  already: "border-ok/40",
  pending: "border-warn",
  preview: "border-dashed border-accent/70",
  error: "border-err/70",
};
const ЗНАЧОК: Record<string, string> = {
  applied: "bg-ok/15 text-ok",
  already: "bg-ok/10 text-fg",
  pending: "bg-warn-bg text-fg",
  preview: "bg-accent/10 text-fg",
  error: "bg-err/10 text-err",
};
const СТАТУС_ССЫЛКИ: Record<string, string> = { sent: "Отправлено", error: "Не ушло", no_chat: "Нет чата канала", preview: "Не отправлено (проба)", wait: "Ждёт отправки", work: "Отправляется", has_link: "Ссылка уже есть" };

/** Действие робота в CRM карточкой: что сделал, «было → стало» и записано ли в Bitrix на самом деле. */
export function ActionCard({
  a,
  seek,
  openDeal,
}: {
  a: LabAction;
  seek?: ((t: number) => void) | undefined;
  openDeal?: ((id: number) => void) | undefined;
}) {
  const подпись =
    a.status_label ??
    (a.kind === "link" ? (СТАТУС_ССЫЛКИ[a.status ?? ""] ?? a.status) : a.status === "done" ? "Сделано" : "Ждёт коллегу");
  return (
    <div
      className={`flex flex-col gap-1 rounded-[var(--radius-ui)] border bg-bg px-3 py-2 text-[12.5px] sm:ml-6 ${ЦВЕТ_ДЕЙСТВИЯ[a.status ?? ""] ?? "border-border"}`}
      data-annot="действие робота"
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <Метка t={a.t} seek={seek} />
        <span className="text-fg-muted">Робот →{a.kind === "link" || a.kind === "problem" ? "" : " CRM:"}</span>
        <b>{a.title}</b>
        {подпись && (
          <span className={`rounded-full px-2 py-px text-[11.5px] ${ЗНАЧОК[a.status ?? ""] ?? "bg-bg-subtle text-fg-muted"}`}>
            {подпись}
          </span>
        )}
        {a.deal_id && (
          openDeal ? (
            <button type="button" onClick={() => openDeal(a.deal_id!)} className="text-accent hover:underline">
              сделка {a.deal_id}
            </button>
          ) : (
            <span className="text-fg-muted">сделка {a.deal_id}</span>
          )
        )}
        {a.lead_id && <span className="text-fg-muted">лид {a.lead_id}</span>}
      </div>
      {a.changes.length > 0 && (
        <div className="text-[11.5px] text-fg-muted">
          {a.status === "applied"
            ? "Изменения"
            : a.status === "error"
              ? "Попытка изменения"
              : a.status === "already"
                ? "Уже было в сделке"
                : "План изменений — в сделке этого пока нет"}
        </div>
      )}
      {a.changes.length > 0 && (
        <ul className="flex flex-col gap-0.5">
          {a.changes.map((c) => (
            <li key={c.field} className="flex flex-wrap gap-x-1.5">
              <span className="text-fg-muted">{c.field}:</span>
              <span
                className={c.before === "—" || c.before === "неизвестно" ? "text-fg-muted" : "line-through decoration-fg-muted/60"}
              >
                {c.before}
              </span>
              <span className="text-fg-muted">→</span>
              <b>{c.after}</b>
            </li>
          ))}
        </ul>
      )}
      {a.status === "preview" && (
        <div className="text-[11.5px] text-fg-muted">
          Запись в Bitrix для этого звонка была выключена — так робот сделал бы. В сделке ничего не поменялось.
        </div>
      )}
      {(!a.changes.length || a.status === "error" || a.status === "no_target" || a.status === "ambiguous") && a.plan && (
        <div className="whitespace-pre-wrap text-[12px] text-fg-muted">{a.plan}</div>
      )}
    </div>
  );
}

/** Служебный шаг (набор номера, каналы клиента, эхоподавление) — тихой строкой, человеческим языком. */
function Служебный({ e, seek }: { e: Инструмент; seek?: ((t: number) => void) | undefined }) {
  const a = e.args as Record<string, unknown>;
  const текст =
    e.name === "dial"
      ? `Набор номера ${String(a["phone"] ?? "")}: ${e.result} за ${(e.ms / 1000).toFixed(1).replace(".", ",")} с`
      : e.name === "client_channels"
        ? `Каналы клиентки: ${e.result}`
        : e.name === "aec"
          ? `Эхоподавление: задержка эха ${String(a["delay_ms"] ?? "—")} мс, подавлено ${String(a["echo_suppressed_db"] ?? "—")} дБ`
          : `${e.name}: ${e.result}`;
  return (
    <div className="text-[11.5px] text-fg-muted sm:ml-6">
      <Метка t={e.t} seek={seek} />
      {текст}
    </div>
  );
}
const СЛУЖЕБНЫЕ = new Set(["dial", "client_channels", "aec"]);

function расходится(e: Реплика): boolean {
  if (!e.heard) return !!e.text && e.t != null;
  return !wordDiff(e.text || "", e.heard).equal;
}

/** Лента «Обзора»: реплики (сверка с записью GigaAM — по переключателю, по умолчанию выключена), действия
 * робота в CRM карточками, служебные шаги тихо, замечания у реплик и у моментов записи, конец звонка. */
function Лента({
  c,
  seek,
  форма,
  setФорма,
  openDeal,
}: {
  c: LabCallCard;
  seek?: ((t: number) => void) | undefined;
  форма: Форма;
  setФорма: (f: Форма) => void;
  openDeal?: ((id: number) => void) | undefined;
}) {
  const есть_запись = !!c.heard;
  const [сверка, setСверка] = useState(false);
  const [только, setТолько] = useState(false);
  const лишние = c.heard_extra ?? [];
  const действия = new Map((c.actions ?? []).filter((a) => a.tool != null).map((a) => [a.tool!, a]));
  const замечания = c.remarks ?? [];
  const откуда = источники(c);
  const как = черновики(c);
  const конец = (c.row.seconds ?? 0) + 1;
  const items: Элемент[] = [
    ...c.turns.map((t, i) => ({ ...t, kind: "turn" as const, i })),
    // next_work — после разговора, секунды у него нет: в конец ленты, а не в начало
    ...c.tools.map((t, ti) => ({ ...t, kind: "tool" as const, ti, t: t.t ?? (t.name === "next_work" ? конец : null) })),
    ...лишние.map((x) => ({ ...x, kind: "extra" as const, t: x.start })),
    ...замечания
      .filter((r) => r.turn == null && r.t != null)
      .map((r) => ({ kind: "remark" as const, r, t: r.t! })),
    { kind: "end" as const, t: конец + 1 },
  ];
  // По началу: реплика — где фраза началась в записи (`t` журнала — её конец), инструмент — когда его позвали
  // (`t` — конец вызова), набор номера — первым; звонки до 26.10.26 без секунд — по времени события
  // Реплика без своей фразы (слилась с соседней) не обгоняет следующую того же говорящего: порядок журнала
  const ключ: number[] = [];
  const следующая: Record<string, number> = {};
  for (let i = c.turns.length - 1; i >= 0; i--) {
    const t = c.turns[i]!;
    ключ[i] = Math.min(t.heard_start ?? t.t ?? -1, следующая[t.who] ?? Infinity);
    следующая[t.who] = ключ[i]!;
  }
  const начало = (e: Элемент) =>
    e.kind === "turn"
      ? ключ[e.i]!
      : e.kind === "tool"
        ? e.t == null
          ? -1
          : e.t - e.ms / 1000
        : e.t;
  const поСекунде = c.turns.every((t) => t.t != null);
  const есть_at = (e: Элемент): e is Extract<Элемент, { at: string }> => e.kind === "turn" || e.kind === "tool";
  items.sort((a, b) =>
    поСекунде || !есть_at(a) || !есть_at(b) ? начало(a) - начало(b) : a.at.localeCompare(b.at),
  );
  const расхождений = есть_запись ? c.turns.filter(расходится).length + лишние.length : 0;
  const видно =
    сверка && только
      ? items.filter(
          (e) =>
            e.kind === "extra" || (e.kind === "tool" && ПОИСК.has(e.name)) || (e.kind === "turn" && расходится(e)),
        )
      : items;
  return (
    <section className="flex max-w-3xl flex-col gap-2" data-annot="лента разговора">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        {есть_запись ? (
          <>
            <label className="flex cursor-pointer items-center gap-1.5" title="Под текстом модели — что прозвучало в записи по независимой расшифровке GigaAM">
              <input type="checkbox" checked={сверка} onChange={(e) => setСверка(e.target.checked)} />
              Сверка с записью (GigaAM)
            </label>
            {сверка && (
              <>
                <button type="button" className={кнопка(!только)} onClick={() => setТолько(false)}>
                  Все
                </button>
                <button type="button" className={кнопка(только)} onClick={() => setТолько(true)}>
                  Расхождения · {расхождений}
                </button>
              </>
            )}
            {сверка && (
              <span className="text-[11.5px] text-fg-muted">
                GigaAM — независимая расшифровка записи; различия проверяйте на слух. Время ведёт к началу фразы в
                записи.
              </span>
            )}
          </>
        ) : c.audio ? (
          <span className="text-fg-muted">Расшифровка записи ещё не готова — сверка появится сама.</span>
        ) : c.row.turn === "text" ? (
          <span className="text-fg-muted">Переписка в Telegram — звука нет, сверять не с чем</span>
        ) : (
          <span className="text-fg-muted">Записи нет — сверять не с чем</span>
        )}
      </div>
      {видно.map((e, i) =>
        e.kind === "tool" ? (
          действия.has(e.ti) ? (
            <ActionCard key={i} a={действия.get(e.ti)!} seek={seek} openDeal={openDeal} />
          ) : СЛУЖЕБНЫЕ.has(e.name) ? (
            <Служебный key={i} e={e} seek={seek} />
          ) : (
            <ToolStep key={i} e={e} seek={seek} курс={c.rub?.rate} салон={c.row.location_id} />
          )
        ) : e.kind === "extra" ? (
          сверка && <ТолькоВЗаписи key={i} e={e} seek={seek} />
        ) : e.kind === "remark" ? (
          <div key={i} className="flex flex-col items-center gap-1">
            <RemarkChip r={e.r} seek={seek} />
          </div>
        ) : e.kind === "end" ? (
          <div key={i} className="flex flex-col gap-2">
            {(c.actions ?? [])
              .filter((a) => a.tool == null)
              .map((a, n) => (
                <ActionCard key={n} a={a} openDeal={openDeal} />
              ))}
            <div className="border-t border-border pt-1.5 text-center text-[12px] text-fg-muted" data-annot="конец звонка">
              Звонок завершён · {длительность(c.row.seconds)} · итог <b className="text-fg">{OUTCOMES[c.row.outcome] ?? c.row.outcome}</b>
              {c.row.end_reason && ` · ${END_REASONS[c.row.end_reason] ?? c.row.end_reason}`}
            </div>
          </div>
        ) : (
          <Пузырь
            key={i}
            e={e}
            i={e.i}
            room={c.room}
            seek={seek}
            сверка={сверка}
            есть_запись={есть_запись}
            замечания={замечания.filter((r) => r.turn === e.i)}
            форма={!!форма && "turn" in форма && форма.turn === e.i}
            setФорма={setФорма}
            источник={откуда.get(e.i)}
            черновик={как.get(e.i)}
          />
        ),
      )}
      {только && !видно.length && <div className="text-[12.5px] text-fg-muted">Расхождений нет</div>}
    </section>
  );
}

const ЦВЕТ = ["bg-accent", "bg-accent/60", "bg-ok", "bg-ok/50", "bg-warn", "bg-err", "bg-fg-muted", "bg-accent/30"];

const доля = (p: number) => (p > 0 && p < 0.1 ? "<0,1 %" : `${p.toFixed(1).replace(".", ",")} %`);

/** Вкладка «Расход»: статьи в ₽ по курсу ЦБ на день разговора, доля каждой и полоса долей; $ — мелко. */
function Расход({ c }: { c: LabCallCard }) {
  const cost = c.cost;
  const курс = c.rub?.rate ?? 0;
  const сумма = итог(c);
  const шагов = c.tools.filter((t) => ПОИСК.has(t.name)).length;
  const строки: { what: string; объём: string; usd: number | null }[] = [
    ...(cost?.rows ?? []).map((s) => ({
      what: s.what,
      объём: s.tokens != null ? `${s.tokens.toLocaleString("ru-RU")} ток.` : "",
      usd: s.usd,
    })),
    ...(шагов
      ? [
          {
            what: `поиск по базе знаний · эмбеддинг вопроса (${шагов})`,
            объём: сумма.запросов
              ? `${c.tools.reduce((s, t) => s + (t.embed?.tokens ?? 0), 0).toLocaleString("ru-RU")} ток.`
              : "не сохранён",
            usd: сумма.запросов ? сумма.поиск : null,
          },
        ]
      : []),
  ];
  const всего = сумма.usd;
  if (!cost && !шагов) return <div className="text-[12.5px] text-fg-muted">Расход у этого разговора не записан</div>;
  return (
    <section className="flex max-w-3xl flex-col gap-3 text-[12.5px]" data-annot="расход разговора">
      <div className="flex flex-wrap items-baseline gap-x-3">
        <span className="text-[16px] font-semibold tabular-nums">
          {всего != null ? `${сумма.неоценён ? "Известно" : "Учтено"} ${rub(всего, курс)}` : "Цена модели не задана"}
        </span>
        {всего != null && cost && cost.seconds > 0 && (
          <span className="tabular-nums text-fg-muted">
            · {rub((всего * 60) / cost.seconds, курс)}/мин · {cost.seconds} с · {usd(всего)}
          </span>
        )}
      </div>
      {всего != null && всего > 0 && (
        <>
          <div className="text-fg-muted">{сумма.неоценён ? "Доли известной суммы; поиск не оценён" : "Доли учтённого расхода"}</div>
          <div className="flex h-2.5 w-full overflow-hidden rounded-[3px] bg-border" aria-hidden>
            {строки.map((s, i) =>
              s.usd ? (
                <div key={s.what} className={ЦВЕТ[i % ЦВЕТ.length]} style={{ width: `${(s.usd / всего) * 100}%` }} title={`${s.what}: ${доля((s.usd / всего) * 100)}`} />
              ) : null,
            )}
          </div>
        </>
      )}
      <table className="w-full">
        <thead className="hidden sm:table-header-group">
          <tr className="border-b border-border text-left text-fg-muted">
            <th className="py-1.5 pr-3 font-medium">На что</th>
            <th className="py-1.5 pr-3 text-right font-medium">Объём</th>
            <th className="py-1.5 pr-3 text-right font-medium">Сумма, ₽</th>
            <th className="py-1.5 text-right font-medium">Доля</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((s, i) => (
            <tr key={s.what} className="block border-b border-border py-1.5 sm:table-row sm:py-0">
              <td className="block sm:table-cell sm:py-1.5 sm:pr-3">
                <span className={`mr-2 inline-block size-2 rounded-full ${ЦВЕТ[i % ЦВЕТ.length]}`} />
                {s.what}
              </td>
              <td className="inline sm:table-cell sm:py-1.5 sm:pr-3 sm:text-right">
                <span className="text-fg-muted sm:hidden">Объём: </span>
                <span className="tabular-nums text-fg-muted">{s.объём || "—"}</span>
              </td>
              <td className="inline pl-3 sm:table-cell sm:py-1.5 sm:pr-3 sm:pl-0 sm:text-right">
                <span className="text-fg-muted sm:hidden">Сумма: </span>
                <span className="tabular-nums" title={s.usd != null ? usd(s.usd) : undefined}>
                  {s.usd != null ? rub(s.usd, курс) : "неизвестна"}
                </span>
              </td>
              <td className="inline pl-3 sm:table-cell sm:py-1.5 sm:pl-0 sm:text-right">
                <span className="text-fg-muted sm:hidden">Доля: </span>
                <span className="tabular-nums">{s.usd != null && всего ? доля((s.usd / всего) * 100) : "—"}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-col gap-1 text-[11.5px] text-fg-muted">
        <span>
          Учтены модель, распознавание речи собеседника (gpt-transcribe) и эмбеддинги поиска по базе. Не входят
          телефония, сервер и расшифровка записи GigaAM; LightRAG свою сумму не отдаёт.
        </span>
        <span>
          Курс {курс.toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ₽ за $ · {c.rub?.source ?? "—"}
          {c.rub?.date ? ` на ${c.rub.date.split("-").reverse().join(".")} (день разговора или ближайший рабочий раньше)` : " · дата курса не передана"}
        </span>
        {cost?.source && <span>Цены модели: {cost.source}</span>}
      </div>
    </section>
  );
}

export function CallCard({ room, back }: { room: string; back: () => void }) {
  const q = useQuery({
    ...labCallQuery(room),
    // Запись есть, расшифровки ещё нет — переспрашиваем, пока вкладка видна, но не дольше 15 минут
    // и пока голос замечания расшифровывается — статус сменится сам
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d || document.hidden) return false;
      if (d.remarks?.some((r) => r.status === "waiting_voice")) return 4_000;
      if (d.heard || !d.audio) return false;
      const конец = new Date(d.row.started).getTime() + (d.row.seconds ?? 0) * 1000;
      return Date.now() - конец < ЖДЁМ_РАСШИФРОВКУ ? 5_000 : false;
    },
  });
  const [params, setParams] = useSearchParams();
  const ctab = params.get("ctab") === "log" ? "actions" : (params.get("ctab") ?? "overview");
  const [форма, setФорма] = useState<Форма>(null);
  const [позиция, setПозиция] = useState({ t: 0, paused: true });
  const сделка = Number(params.get("deal")) || null;
  const панель = (next: { deal?: number | null; chat?: number | null; side?: string | null }) =>
    setParams((p) => {
      const n = new URLSearchParams(p);
      for (const [k, v] of Object.entries({ deal: next.deal, dchat: next.chat, dside: next.side })) {
        if (v === undefined) continue;
        if (v == null || v === "") n.delete(k);
        else n.set(k, String(v));
      }
      return n;
    });
  const вкладка = (t: string) =>
    setParams((p) => {
      const n = new URLSearchParams(p);
      if (t === "overview") n.delete("ctab");
      else n.set("ctab", t);
      return n;
    });
  const player = useRef<HTMLAudioElement>(null);
  const seek = (t: number) => {
    const a = player.current;
    if (!a) return;
    a.currentTime = t;
    a.play().catch(() => undefined); // браузер не дал играть без жеста — позиция всё равно стоит
  };
  const c = q.data;
  const сумма = c ? итог(c) : null;
  const openDeal = (id: number) => панель({ deal: id, chat: null, side: null });
  const нет = q.error instanceof ApiFailure && q.error.status === 404;
  return (
    <section className="flex flex-col gap-3" data-annot="карточка разговора">
      <button type="button" onClick={back} className="w-fit text-[12.5px] text-fg-muted hover:text-fg">
        ← Журнал
      </button>
      {q.isError &&
        (нет ? (
          <div className="text-[12.5px] text-err">Разговора нет в журнале за 30 дней</div>
        ) : (
          <div className="text-[12.5px] text-err">
            Не удалось загрузить разговор ·{" "}
            <button type="button" onClick={() => void q.refetch()} className="text-accent hover:underline">
              Повторить
            </button>
          </div>
        ))}
      {c && (
        <>
          <div className="flex flex-col gap-0.5">
            <div className="text-[14px] font-semibold">
              {c.row.name}
              <span className="ml-1.5 font-normal text-fg-muted">
                {c.row.phone ? `☎ ${телефон(c.row.phone)}` : c.row.turn === "text" ? "Telegram · текст" : "браузер"}
              </span>
            </div>
            <div className="flex flex-wrap items-baseline gap-x-2 text-[12px] text-fg-muted">
              <span>
                {когда(c.row.started)} МСК{c.row.location_name && ` · ${c.row.location_name}`}
              </span>
              {c.deal ? (
                <button
                  type="button"
                  onClick={() => openDeal(c.deal!.id)}
                  className="rounded-full border border-border px-2 py-px text-[12.5px] text-fg hover:border-accent"
                  title="Открыть сделку и чат клиента справа"
                  data-annot="сделка звонка"
                >
                  Сделка {c.deal.id}
                  {c.deal.stage_name && <span className="text-fg-muted"> · {c.deal.stage_name}</span>}
                  <span className="text-accent"> →</span>
                </button>
              ) : (
                <span>без сделки</span>
              )}
              <span title="Версия инструкции: имя шаблона и хэш собранного текста — вкладка «Инструкция»">
                инструкция {c.row.prompt ?? "—"}
              </span>
              <span
                className={c.row.lightrag?.empty ? "text-warn" : ""}
                title="Поиск робота в базе знаний LightRAG (search_knowledge) за разговор"
                data-annot="поиск в LightRAG"
              >
                {c.row.lightrag?.n
                  ? `LightRAG: ${c.row.lightrag.n}${c.row.lightrag.empty ? `, не нашёл ${c.row.lightrag.empty}` : ""}`
                  : "LightRAG: не обращался"}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Карточка разговора">
            {[
              ["overview", "Обзор"],
              ["cost", сумма?.usd != null && c.rub ? `Расход · ${rub(сумма.usd, c.rub.rate)}` : "Расход"],
              ["actions", c.actions?.length ? `Действия · ${c.actions.length}` : "Действия"],
              ["pbx", c.pbx ? `Лог звонка · ${c.pbx.outcome_label}` : "Лог звонка"],
              ["remarks", c.remarks?.length ? `Замечания · ${c.remarks.length}` : "Замечания"],
              ["prompt", "Инструкция"],
            ].map(([код, имя]) => (
              <button key={код} type="button" role="tab" aria-selected={ctab === код} className={кнопка(ctab === код)} onClick={() => вкладка(код!)}>
                {имя}
              </button>
            ))}
          </div>
          {ctab === "overview" && (
            <>
              <Сводка c={c} toCost={() => вкладка("cost")} toPrompt={() => вкладка("prompt")} seek={c.audio ? seek : undefined} />
              {!!c.links?.length && <Ссылки links={c.links} />}
              {c.problems?.map((p) => (
                <div key={p.id} className="text-[12.5px]" data-annot="вопрос передан коллеге">
                  Передано коллеге ({p.kind === "complaint" ? "жалоба" : p.kind === "no_answer" ? "не смог ответить" : "другое"}):{" "}
                  {p.summary} ·{" "}
                  <a href="/voice-lab?tab=problems" className="text-accent hover:underline">
                    {p.status === "done" ? "сделано" : "ждёт коллегу"}
                  </a>
                </div>
              ))}
            </>
          )}
          {/* плеер не размонтируем при смене вкладки — позиция записи сохраняется */}
          <div className={ctab === "overview" || ctab === "remarks" ? "flex flex-col gap-2" : "hidden"}>
            {c.audio ? (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-annot="запись разговора">
                <audio
                  ref={player}
                  controls
                  preload="metadata"
                  src={labAudioUrl(room)}
                  className="h-9 w-full sm:max-w-md"
                  onTimeUpdate={(e) => setПозиция({ t: e.currentTarget.currentTime, paused: e.currentTarget.paused })}
                  onPause={(e) => setПозиция({ t: e.currentTarget.currentTime, paused: true })}
                  onPlay={(e) => setПозиция({ t: e.currentTarget.currentTime, paused: false })}
                />
                <button
                  type="button"
                  onClick={() => {
                    player.current?.pause();
                    setФорма({ t: Math.round((player.current?.currentTime ?? позиция.t) * 10) / 10 });
                  }}
                  className={`flex items-center gap-1 rounded-full border px-3 py-1 text-[12.5px] ${
                    позиция.paused && позиция.t > 0 ? "border-accent bg-accent/10 text-fg" : "border-border text-accent hover:border-accent"
                  }`}
                  title="Замечание к этому месту записи — голосом или текстом; запись встанет на паузу"
                  data-annot="замечание к моменту записи"
                >
                  <MessageSquarePlus size={14} aria-hidden /> Замечание на {мин(позиция.t)}
                </button>
                <a href={labMp3Url(room)} download className="text-[12.5px] text-accent hover:underline">
                  Скачать MP3
                </a>
                <a href={labAudioUrl(room)} download aria-label="Скачать OGG" className="text-[12.5px] text-fg-muted hover:text-fg">
                  OGG
                </a>
                <span className="text-[11.5px] text-fg-muted">Стерео: слева собеседник, справа робот</span>
              </div>
            ) : c.row.turn !== "text" ? (
              <div className="text-[12.5px] text-fg-muted">Запись не сохранена. Доступен текст журнала.</div>
            ) : null}
            {форма && "t" in форма && (
              <RemarkForm room={room} t={форма.t} onClose={() => setФорма(null)} />
            )}
          </div>
          {ctab === "cost" ? (
            <Расход c={c} />
          ) : ctab === "actions" ? (
            <section className="flex max-w-3xl flex-col gap-2" data-annot="действия робота">
              {c.actions?.length ? (
                c.actions.map((a, n) => <ActionCard key={n} a={a} seek={c.audio ? seek : undefined} openDeal={openDeal} />)
              ) : (
                <div className="text-[12.5px] text-fg-muted">Робот в CRM ничего не делал и ссылок не обещал.</div>
              )}
              <details className="mt-2 text-[12.5px]">
                <summary className="cursor-pointer text-fg-muted">Технический журнал: параметры и ответы</summary>
                <div className="mt-2">
                  <VoiceLabCallLog room={room} />
                </div>
              </details>
            </section>
          ) : ctab === "pbx" ? (
            <PbxTab pbx={c.pbx} room={room} phone={c.row.phone} />
          ) : ctab === "remarks" ? (
            <RemarksTab remarks={c.remarks ?? []} seek={c.audio ? seek : undefined} />
          ) : ctab === "prompt" ? (
            c.instructions ? (
              <section className="flex max-w-3xl flex-col gap-1 text-[12.5px]" data-annot="инструкция разговора">
                <div className="text-fg-muted">
                  Инструкция, ушедшая модели · версия {c.row.prompt ?? "—"} · {c.instructions.length.toLocaleString("ru")}{" "}
                  знаков
                </div>
                <pre className="whitespace-pre-wrap break-words rounded-[var(--radius-ui)] border border-border bg-bg-subtle p-3 font-sans leading-relaxed">
                  {c.instructions}
                </pre>
              </section>
            ) : (
              <div className="text-[12.5px] text-fg-muted">Инструкция этого разговора не сохранена</div>
            )
          ) : (
            <>
              <ChecklistBox checklist={c.checklist} className="max-w-3xl" />
              <Лента c={c} seek={c.audio ? seek : undefined} форма={форма} setФорма={setФорма} openDeal={openDeal} />
            </>
          )}
          {сделка && (
            <DealSide
              dealId={сделка}
              chatId={Number(params.get("dchat")) || (c.deal?.id === сделка ? c.deal.chat_id : null)}
              tab={params.get("dside") === "deal" ? "deal" : "chat"}
              title={
                <>
                  Сделка {сделка}
                  <span className="font-normal text-fg-muted"> · звонок робота {c.row.name ?? ""}</span>
                </>
              }
              onTab={(tab) => панель({ side: tab })}
              onChat={(id) => панель({ chat: id })}
              onClose={() => панель({ deal: null, chat: null, side: null })}
            />
          )}
        </>
      )}
    </section>
  );
}
